import { useState, useMemo, useEffect, useRef } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import moment from 'moment';
import { message } from 'antd';
import { bookingJourneyType } from 'common/constants/booking';
import {
  QUERY_LANDLORD_PROPERTIES,
  QUERY_PROPERTY_STATISTICS,
  QUERY_BOOKINGS_STATISTICS,
  QUERY_LANDLORD_ORDERS,
  QUERY_LANDLORD_BOOKINGS,
  QUERY_INSTANT_ORDERS,
  QUERY_LEADGEN_ORDERS,
} from '../queries';
import event from '~helpers/mixpanel';
import { pageType } from '~constants/pages';

export const orderAll = 'All';

export const opportunityStages = {
  COMPLETED: 'COMPLETED',
  CANCELLATION: 'CANCELLATION',
};

const defaultOpportunityStages = [opportunityStages.COMPLETED, opportunityStages.CANCELLATION];

const dropDownPageSize = 20;

const defaultOtherTableFilter = {
  pageNumber: 1,
  pageSize: 10,
  sortBy: 'CREATED_AT',
};

const createdAfter = moment().subtract(6, 'days').format('YYYY-MM-DD');

const useTableFilters = () => {
  const [bookingType, setBookingType] = useState(bookingJourneyType.LEAD_GENERATION);
  const [bookingStage, setBookingStage] = useState(orderAll);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [otherTableFilter, setOtherTableFilter] = useState(defaultOtherTableFilter);
  const [propertyNamePageNumber, setPropertyNamePageNumber] = useState(1);
  const [propertyQuery, setPropertyQuery] = useState('');
  const [propertyNameOptions, setPropertyNameOptions] = useState([]);

  const hasMore = useRef(true);

  const isHerculesBooking = useMemo(
    () => bookingType === bookingJourneyType.SEMI_AUTOMATIC,
    [bookingType],
  );

  const isLeadBooking = useMemo(
    () => bookingType === bookingJourneyType.LEAD_GENERATION,
    [bookingType],
  );

  const isInstantBooking = useMemo(
    () => bookingType === bookingJourneyType.INSTANT_BOOK,
    [bookingType],
  );

  const isManualBooking = useMemo(() => bookingType === bookingJourneyType.MANUAL, [bookingType]);

  const [queryPropertyStatistics, { data: propertyStatisticsData }] = useLazyQuery(
    QUERY_PROPERTY_STATISTICS,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const { data: landlordProperties, loading: isLandlordPropertiesLoading } = useQuery(
    QUERY_LANDLORD_PROPERTIES,
    {
      variables: {
        name: propertyQuery,
        pageNumber: propertyNamePageNumber,
        pageSize: dropDownPageSize,
      },
      fetchPolicy: 'network-only',
    },
  );

  const [queryBookingStatistics, { loading: statisticsLoading, data: statisticsData }] =
    useLazyQuery(QUERY_BOOKINGS_STATISTICS, {
      fetchPolicy: 'no-cache',
      variables: {
        createdAfter,
      },
      onCompleted: data => {
        const hasHerculesOrder = data?.viewer?.company?.herculesAllOrders?.totalCount > 0;
        const hasManualOrder = data?.viewer?.company?.manualAllBookings?.totalCount > 0;
        const hasInstantOrder = data?.viewer?.company?.instantAllOrders?.totalCount > 0;
        const hasLeadOrder = data?.viewer?.company?.leadGenAllOrders?.totalCount > 0;

        if (hasLeadOrder) {
          setBookingType(bookingJourneyType.LEAD_GENERATION);
          return;
        }

        if (hasHerculesOrder) {
          setBookingType(bookingJourneyType.SEMI_AUTOMATIC);
          return;
        }

        if (hasInstantOrder) {
          setBookingType(bookingJourneyType.INSTANT_BOOK);
          return;
        }

        if (hasManualOrder) {
          setBookingType(bookingJourneyType.MANUAL);
          return;
        }
      },
    });

  const variables = useMemo(
    () => {
      const queryArguments = {
        ...otherTableFilter,
      };

      if (selectedProperties?.length > 0) {
        queryArguments.propertyIds = selectedProperties;
      }

      if (isHerculesBooking) {
        queryArguments.stages = bookingStage !== orderAll ? [bookingStage] : undefined;
        queryArguments.createdAfter = createdAfter;
      } else if (isInstantBooking) {
        queryArguments.stages = bookingStage !== orderAll ? [bookingStage] : undefined;
      } else if (isLeadBooking) {
        queryArguments.stages = undefined;
      } else {
        queryArguments.opportunityStages =
          bookingStage !== orderAll ? [bookingStage] : defaultOpportunityStages;
      }

      return queryArguments;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      bookingStage,
      isHerculesBooking,
      isInstantBooking,
      isLeadBooking,
      isManualBooking,
      otherTableFilter,
      selectedProperties,
    ],
  );
  //  leadProperty;
  //  LeadGenAllOrders;
  const [
    queryLandlordOrders,
    { data: orders, loading: queryOrdersLoading, refetch: refetchLandlordOrders },
  ] = useLazyQuery(QUERY_LANDLORD_ORDERS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const [queryInstantOrders, { data: instantOrders, loading: queryInstantLoading }] = useLazyQuery(
    QUERY_INSTANT_ORDERS,
    {
      fetchPolicy: 'no-cache',
      onError: e => {
        message.error(e.toString());
      },
    },
  );

  const [queryLeadOrders, { data: leadOrders, loading: leadOrdersLoading }] = useLazyQuery(
    QUERY_LEADGEN_ORDERS,
    {
      fetchPolicy: 'no-cache',
      onError: e => {
        message.error(e.toString());
      },
    },
  );

  const [
    queryLandlordBookings,
    { data: bookings, loading: queryBookingsLoading, refetch: refetchLandlordBookings },
  ] = useLazyQuery(QUERY_LANDLORD_BOOKINGS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const pendingOrderCount = useMemo(() => {
    const { pendingConfirmationOrderCount, pendingRefundOrderCount } =
      orders?.viewer?.company ?? {};

    return {
      confirmation: pendingConfirmationOrderCount?.totalCount,
      refund: pendingRefundOrderCount?.totalCount,
    };
  }, [orders?.viewer?.company]);

  const bookingTableData = useMemo(() => {
    if (isHerculesBooking) {
      return orders;
    } else if (isInstantBooking) {
      return instantOrders;
    } else if (isLeadBooking) {
      return leadOrders;
    } else {
      return bookings;
    }
  }, [
    isHerculesBooking,
    orders,
    isInstantBooking,
    instantOrders,
    leadOrders,
    isLeadBooking,
    bookings,
  ]);

  const changeBookingType = value => {
    setBookingType(value);
    setBookingStage(orderAll);
    setOtherTableFilter(defaultOtherTableFilter);
  };

  const resetPageNumber = () => {
    setOtherTableFilter({ ...otherTableFilter, pageNumber: 1 });
  };

  const changeBookingStage = value => {
    setBookingStage(value ?? orderAll);
    resetPageNumber();
  };

  const changeSelectedProperties = value => {
    setSelectedProperties(value);
    resetPageNumber();
  };

  const handleScrollEnd = () => {
    if (hasMore.current) {
      setPropertyNamePageNumber(prev => prev + 1);
    }
  };

  const handleInputSearch = val => {
    setPropertyNameOptions([]);
    setPropertyNamePageNumber(1);
    setPropertyQuery(val);
    hasMore.current = true;
  };

  useEffect(() => {
    if (!isLandlordPropertiesLoading) {
      const properties = (landlordProperties?.properties?.edges ?? []).map(item => ({
        value: item.node.id,
        label: item.node.name,
      }));

      if (properties.length < dropDownPageSize) {
        hasMore.current = false;
      } else {
        hasMore.current = true;
      }
      if (properties.length) {
        setPropertyNameOptions(prev => [...prev, ...properties]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandlordPropertiesLoading]);

  useEffect(() => {
    queryBookingStatistics({
      variables: {
        propertyIds: selectedProperties?.length > 0 ? selectedProperties : undefined,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryBookingStatistics, selectedProperties]);

  useEffect(
    () => {
      if (isHerculesBooking) {
        queryLandlordOrders({ variables });
      } else if (isInstantBooking) {
        queryInstantOrders({ variables });
      } else if (isLeadBooking) {
        queryLeadOrders({ variables });
      } else {
        queryLandlordBookings({ variables });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isHerculesBooking,
      isInstantBooking,
      queryLandlordBookings,
      queryLandlordOrders,
      isLeadBooking,
      queryInstantOrders,
      otherTableFilter,
      variables,
    ],
  );

  useEffect(() => {
    queryPropertyStatistics();
    queryBookingStatistics();
  }, [queryPropertyStatistics, queryBookingStatistics]);

  useEffect(() => {
    event('Viewed Booking List Page', {
      'Booking Journey': bookingType?.toLowerCase() || null,
      'Current Page Type': pageType.bookingListPage,
    });
  }, [bookingType]);

  return {
    bookingType,
    setBookingType: changeBookingType,
    bookingStage,
    setBookingStage: changeBookingStage,
    otherTableFilter,
    setOtherTableFilter,
    handleInputSearch,
    selectedProperties,
    setSelectedProperties: changeSelectedProperties,
    statisticsLoading,
    propertyStatisticsData: propertyStatisticsData?.viewer ?? [],
    statisticsData: statisticsData?.viewer ?? {},
    propertyNameOptions,
    pendingOrderCount,
    tableData: bookingTableData,
    tableLoading:
      queryOrdersLoading || queryBookingsLoading || queryInstantLoading || leadOrdersLoading,
    refetchTableData: isHerculesBooking ? refetchLandlordOrders : refetchLandlordBookings,
    handleScrollEnd,
    isLandlordPropertiesLoading,
  };
};

export default useTableFilters;
